import React, { useContext } from 'react'
import { PurchaseHistoryCard, PurchaseHistoryCardCol, PurchaseHistoryFormRow } from './PurchaseHistoryDetail.styled'
import VehicleDetailImage from 'modules/DealerVehicleManagement/VehicleDetailImage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Container, Form, InputGroup, OverlayTrigger, Popover, Row } from 'react-bootstrap'
import { StyledInfoLabel } from 'modules/DealerVehicleManagement/VehicleDetails.styled'
import { useFetch } from 'hooks/useFetch'
import { getOrderHubStatuses, getPurchaseHistory, saveDealerPurchaseRetailInfo } from 'apis/userApis'
import { formatDateTime } from 'utils/dateUtils'
import { Loader } from 'components/Loader'
import { useRef, useState, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import readyauto from 'images/ReadyLogistics.png'
import { PURCHASE_HISTORY_INIT_QUERY, READY_LOGISTIC_LINK } from 'common/constants'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import 'bs-stepper/dist/css/bs-stepper.min.css'
import { DateTimePicker } from 'components/DateTimePicker'
import { FormInput } from 'components/Forms'
import { FormattedMessage } from 'react-intl'
import { Formik, FormikHelpers } from 'formik'
import { useGlobalStore } from 'store/useGlobalStore'
import shallow from 'zustand/shallow'
import * as Yup from 'yup'
import SpinnerButton from 'components/Buttons/SpinnerButton'
import { DismissableAlert } from 'components/DismissableAlert/DismissableAlert'
import { TransportProvider, ICustomerRetailInfo, IOrderHubStatusResponse } from 'types/purchaseHistoryTypes'
import { PurchaseHistoryContext } from 'contexts/PurchaseHistoryContext'

interface IProps {
  VIN: string
  isLoadApi: boolean
}
export const PurchaseHistoryInfoSection = ({ VIN, isLoadApi }: IProps) => {
  const { isRetailVisible } = useContext(PurchaseHistoryContext)
  const { loading: vehicleLoading, data: vehicleData } = useFetch(
    () => getPurchaseHistory({ ...PURCHASE_HISTORY_INIT_QUERY, Vin: VIN, FromDate: null, ToDate: null }),
    [],
    [VIN]
  )
  const getLocalText = useGlobalStore((state) => state.getLocalText)
  const vehicle = vehicleData?.Items[0]
  const [userClaims, vendor] = useGlobalStore((state) => [state.userClaims, state.vendor], shallow)
  const [loading, setLoading] = useState<boolean>(false)
  const [isSaveSuccess, setIsSaveSuccess] = useState(false)
  const [responseMessage, setResponseMessage] = useState('')
  const { loading: orderHubLoading, data } = useFetch(
    () => (isLoadApi ? getOrderHubStatuses({ VehicleInstanceID: vehicle?.VehicleInstanceID, Vin: VIN }) : null),
    [],
    [vehicle, isLoadApi]
  )
  const handleSubmit = async (values: ICustomerRetailInfo, formikHelpers: FormikHelpers<ICustomerRetailInfo>) => {
    if (!!values) {
      setLoading(true)
      try {
        const response = await saveDealerPurchaseRetailInfo(values)
        if (response.Value > 0) {
          setIsSaveSuccess(true)
          setResponseMessage('Save Successfully')
        }
      } catch (error) {
        setIsSaveSuccess(false)
        setResponseMessage('Save Fail')
      }
      setLoading(false)
    }
  }

  const formSchema = Yup.object().shape({
    RetailCustomerName: Yup.string().trim().min(1, 'Please enter Customer Name').required('Please enter Customer Name'),
    RetailDate: Yup.date().required('Please Choose Retail Date')
  })

  const readyAutoElementRef = useRef(null)

  const getHubStatus = (orderHubStatuses: IOrderHubStatusResponse[]) => {
    if (!orderHubStatuses || orderHubStatuses.length === 0) {
      return null
    }

    return (
      <div className="ready-auto-info">
        <div className="bs-stepper vertical">
          <div className="bs-stepper-header" role="tablist">
            {orderHubStatuses.map((element, index) => (
              <div key={element.VehicleOrderHubStatusID}>
                <div className="step">
                  {element.OrderHubStatusID !== 11 && (
                    <div className="bs-stepper-circle">
                      <div className="fas fa-align-center" aria-hidden="true">
                        <FontAwesomeIcon icon={faCheck} color="white" />
                      </div>
                    </div>
                  )}
                  <div className="info">
                    <div className="bs-stepper-label">{element.MappedStatus}</div>
                    {getStatusDateLabel(element)}
                  </div>
                </div>
                {index < orderHubStatuses.length - 1 && <div className="bs-stepper-line"></div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const getStatusDateLabel = (element: IOrderHubStatusResponse) => {
    const {
      OrderHubStatusID,
      EstimatedPickupDateString,
      SourceEventDateString,
      ActualPickupDateString,
      ActualDeliveryDateString
    } = element

    let dateString: string | undefined | null

    if (OrderHubStatusID < 6) {
      dateString = EstimatedPickupDateString || SourceEventDateString
    } else if (OrderHubStatusID === 6) {
      dateString = ActualPickupDateString || SourceEventDateString
    } else if (OrderHubStatusID >= 7 && OrderHubStatusID < 15) {
      dateString = ActualDeliveryDateString || SourceEventDateString
    } else {
      dateString = SourceEventDateString
    }

    return dateString ? <div className="bs-stepper-label">{dateString}</div> : null
  }

  return (
    <>
      <PurchaseHistoryCard>
        {orderHubLoading || vehicleLoading ? (
          <Loader overlay />
        ) : (
          <Row className="detail-row-section">
            <Col lg={3} className="vehicle-image">
              <VehicleDetailImage
                vehicleInstance={vehicle?.VehicleInstanceID}
                mainImageUrl={vehicle?.ImageURL}
                images={[]}
                vehicleVin={vehicle?.Vin}
              ></VehicleDetailImage>
            </Col>
            <Col className="detail-col-section">
              <Container fluid>
                <Row className="px-5">
                  {(() => {
                    const displayOrder = vehicleData?.DisplayOrder
                    const detailItems = vehicle
                      ? [
                          {
                            labelKey: 'MILEAGE',
                            fallbackLabel: 'Mileage',
                            value: vehicle.Mileage?.toLocaleString(),
                            visible: true
                          },
                          {
                            labelKey: 'SALE_CHANNEL',
                            fallbackLabel: 'Sales Channel',
                            value: vehicle.SaleChannel,
                            visible: true
                          },
                          {
                            labelKey: 'TYPE_OF_SALE',
                            fallbackLabel: 'Type Of Sale',
                            value: vehicle.PurchaseType,
                            visible: displayOrder === 2
                          },
                          {
                            labelKey: 'ORDER_NUMBER',
                            fallbackLabel: 'Order Number',
                            value: String(vehicle.InvoiceNumber ?? ''),
                            visible: displayOrder === 0
                          },
                          {
                            labelKey: 'VEHICLE_LOCATION',
                            fallbackLabel: 'Vehicle Location',
                            value: vehicle.Location,
                            visible: displayOrder !== 2
                          },
                          {
                            labelKey: 'GRADE',
                            fallbackLabel: 'Condition',
                            value: vehicle.VehicleCondition,
                            visible: true
                          },
                          {
                            labelKey: 'TRANSPORTATION_STATUS_LABEL',
                            fallbackLabel: 'Transportation Status',
                            value: vehicle.OrderHubLatestStatus,
                            visible: vehicle.IsReadyLogistics === true,
                            render: (): ReactNode => (
                              <OverlayTrigger
                                rootClose={true}
                                container={readyAutoElementRef}
                                trigger="click"
                                placement="left"
                                overlay={
                                  <Popover id="popover-basic">
                                    <Popover.Title className="ready-auto-title">
                                      <img src={readyauto} className="popover-logo" alt="tranport provider logo" />
                                    </Popover.Title>
                                    <Popover.Content className="ready-auto-content">
                                      <div className="vehicle-info-wrapper">
                                        <div className="ready-auto-img">
                                          <img src={vehicle?.ImageURL} alt="tranport provider logo" />
                                        </div>
                                        <div className="vehicle-info">
                                          <div>{vehicle?.Title}</div>
                                          <div>{vehicle?.Vin}</div>
                                        </div>
                                      </div>
                                      {data?.TotalRecords >= 0 &&
                                        data?.Items[0]?.OrderHubStatuses?.length >= 0 &&
                                        getHubStatus(data.Items[0].OrderHubStatuses)}
                                      <div className="ready-auto-footer">
                                        <Link className="link-history" to={READY_LOGISTIC_LINK} target="_blank">
                                          For more information, please click here to go to Ready Logistics
                                          <br />
                                        </Link>
                                        For immediate assistance please contact Ready Logistics at 480-558-3200
                                        <br />
                                        BGDReadySupport@coxautoinc.com
                                      </div>
                                    </Popover.Content>
                                  </Popover>
                                }
                              >
                                <Row>
                                  <StyledInfoLabel className="ready-auto-link" ref={readyAutoElementRef}>
                                    {getLocalText('TRANSPORTATION_STATUS_LABEL', 'Transportation Status')}:
                                  </StyledInfoLabel>{' '}
                                  {vehicle?.OrderHubLatestStatus}
                                </Row>
                              </OverlayTrigger>
                            )
                          },
                          {
                            labelKey: 'TYPE_OF_TITLE',
                            fallbackLabel: 'Title Type',
                            value: vehicle.ChargeDescription,
                            visible: displayOrder === 2 && !!vehicle.ChargeDescription
                          },
                          {
                            labelKey: 'TITLE_STATUS',
                            fallbackLabel: 'Title Status',
                            value: vehicle.ExternalDeliveryStatus,
                            visible: displayOrder === 2 && !!vehicle.ChargeDescription
                          },
                          {
                            labelKey: 'TRACKING_NUMBER',
                            fallbackLabel: 'Tracking Number',
                            value: vehicle.TrackingNumber,
                            visible: displayOrder === 2 && vehicle.ShowDeliveryInfo === true,
                            render: () => (
                              <Row>
                                <StyledInfoLabel>{getLocalText('TRACKING_NUMBER', 'Tracking Number')}:</StyledInfoLabel>{' '}
                                <a
                                  className="tracking-number"
                                  href={vehicle.TrackingURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {vehicle.TrackingNumber}
                                </a>
                              </Row>
                            )
                          }
                        ].filter((item) => item.visible)
                      : []
                    const basePerCol = Math.floor(detailItems.length / 3)
                    const remainder = detailItems.length % 3
                    const colChunks = [0, 1, 2].map((i) => {
                      const start = i * basePerCol + Math.min(i, remainder)
                      const end = start + basePerCol + (i < remainder ? 1 : 0)
                      return detailItems.slice(start, end)
                    })
                    return colChunks.map((colItems, colIndex) => (
                      <PurchaseHistoryCardCol key={colIndex}>
                        {colItems.map((item) =>
                          item.render ? (
                            <React.Fragment key={item.labelKey}>{item.render()}</React.Fragment>
                          ) : (
                            <Row key={item.labelKey}>
                              <StyledInfoLabel>{getLocalText(item.labelKey, item.fallbackLabel)}:</StyledInfoLabel>{' '}
                              {item.value}
                            </Row>
                          )
                        )}
                      </PurchaseHistoryCardCol>
                    ))
                  })()}
                </Row>
              </Container>
              {isRetailVisible && (
                <PurchaseHistoryFormRow>
                  <Container>
                    <Formik
                      initialValues={
                        {
                          RetailCustomerName: vehicle?.RetailCustomerName ? vehicle?.RetailCustomerName : '',
                          RetailDate: vehicle?.RetailDate ? new Date(vehicle?.RetailDate) : new Date(),
                          CreatedUserName: userClaims.Username,
                          VendorId: vendor.ID,
                          VehicleInstanceId: vehicle?.VehicleInstanceID,
                          BuyerVehiclePurchaseId: vehicle?.BuyerVehiclePurchaseID
                        } as ICustomerRetailInfo
                      }
                      validationSchema={formSchema}
                      onSubmit={handleSubmit}
                    >
                      {(props) => (
                        <Form onSubmit={props.handleSubmit}>
                          <Row>
                            <Col>
                              <Row className="mx-3">
                                <Col lg={6}>
                                  <FormInput
                                    placeholder="Enter Customer Name"
                                    name="RetailCustomerName"
                                    label="Customer Name:"
                                    type="text"
                                    error={props.errors.RetailCustomerName}
                                    onChange={props.handleChange}
                                    value={props.values.RetailCustomerName}
                                    isInvalid={!!props.errors.RetailCustomerName}
                                  />
                                </Col>
                                <Col className="retail-date" lg={6}>
                                  <Form.Group id="RetailDate" className="mb-3">
                                    <Form.Label>
                                      <FormattedMessage id="Retail Date:" />
                                    </Form.Label>
                                    <InputGroup>
                                      <DateTimePicker
                                        className="date-picker-input form-control"
                                        name="RetailDate"
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        selected={props.values.RetailDate}
                                        onChange={(e) => {
                                          props.setFieldValue('RetailDate', new Date(e))
                                        }}
                                        maxDate={new Date()}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {props.errors.RetailDate}
                                      </Form.Control.Feedback>
                                    </InputGroup>
                                  </Form.Group>
                                </Col>
                              </Row>
                              <Row xs={12} className="mx-4 mb-3">
                                <SpinnerButton
                                  style={{ width: '100%' }}
                                  isLoading={loading}
                                  type="submit"
                                  className="form-btn"
                                  variant="primary"
                                >
                                  Submit
                                </SpinnerButton>

                                <Container fluid className="mx-0 px-0 mt-3">
                                  <DismissableAlert
                                    variant={isSaveSuccess ? 'success' : 'danger'}
                                    message={responseMessage}
                                    autoDismissable
                                    onClose={() => setResponseMessage('')}
                                  ></DismissableAlert>
                                </Container>
                              </Row>
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </Formik>
                  </Container>
                </PurchaseHistoryFormRow>
              )}
            </Col>
          </Row>
        )}
      </PurchaseHistoryCard>
    </>
  )
}

export default PurchaseHistoryInfoSection
